import type { CaseSummary } from '@/shared/cors-provenance-api';
import type { ScopesObject } from '@/shared/types';
import { caseFolderNameOf } from '@/shared/case-summary';
import { evidenceCategoryScopeFieldsWithWireTokens } from './evidence-locker-ui-shared-new';

/**
 * The **S3 folder segment** for a whole-case scope tag, when the cases list
 * knows it. Folder paths and other non-tag scopes return undefined so callers
 * pass them through unchanged.
 *
 * Chat ``scopes`` are path prefixes, not tags: assistant-service turns each one
 * into ``searchable_path Glob "{scope}/*"`` (``RetrievalService.build_filters``).
 * So a whole-case scope has to become the case's folder segment — which is its
 * ``folder_token`` once it has one. Sending the display name instead matched
 * nothing for a UUID-folder case and silently dropped that case from the answer,
 * with no error for the analyst to notice (TR-359). It only ever looked correct
 * because a legacy case's folder segment *is* its display name.
 *
 * Matching stays deliberately narrow — an exact ``caseTag``, or a tag as the
 * final path segment. Widening it to also match ``caseName`` would rewrite a
 * plain folder scope that happens to share a case's name.
 */
export function caseFolderNameForChatScopeTag(
  scopeTag: string,
  cases: CaseSummary[] | undefined | null,
): string | undefined {
  const trimmed = scopeTag.trim();
  if (!cases?.length) return undefined;
  for (const entry of cases) {
    const caseTag = entry.caseTag?.trim();
    if (!caseTag) continue;
    if (caseTag === trimmed || caseTag === scopeTag) {
      return caseFolderNameOf(entry) || undefined;
    }
  }
  const lastSeg = scopeTag.split('/').pop()?.trim();
  if (lastSeg && lastSeg !== scopeTag) {
    for (const entry of cases) {
      const caseTag = entry.caseTag?.trim();
      if (caseTag && caseTag === lastSeg) {
        return caseFolderNameOf(entry) || undefined;
      }
    }
  }
  return undefined;
}

/** Resolve case tags to folder segments; leave every other scope string as picked. */
export function mapScopeTagsToFolderNames(
  tags: string[],
  folderNameForTag: (tag: string) => string | undefined,
): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const tag of tags) {
    const trimmed = tag.trim();
    if (!trimmed) continue;
    const final = folderNameForTag(trimmed)?.trim() || trimmed;
    if (seen.has(final)) continue;
    seen.add(final);
    out.push(final);
  }
  return out;
}

/**
 * Scope payload for ``Chat`` ``onSubmit``. File-type fields are included only
 * when content categories are on, and included UI keys stay in
 * ``contentCategories`` (the chat map still expects those, not wire tokens).
 */
export function buildQuestionSubmitScopes(args: {
  scopesAndFiles: ScopesObject | undefined;
  contentCategoriesEnabled: boolean;
  mapScopeTags: (tags: string[]) => string[];
}): ScopesObject {
  const { scopesAndFiles, contentCategoriesEnabled, mapScopeTags } = args;
  const includedScopeCats = contentCategoriesEnabled
    ? [...(scopesAndFiles?.evidenceCategoryScopes ?? [])]
    : [];
  const excludedScopeCats = contentCategoriesEnabled
    ? [...(scopesAndFiles?.excludedEvidenceCategoryScopes ?? [])]
    : [];
  const categoryFieldsForSubmit = contentCategoriesEnabled
    ? evidenceCategoryScopeFieldsWithWireTokens(
        includedScopeCats,
        excludedScopeCats,
        !!scopesAndFiles?.evidenceAllFileTypes,
      )
    : null;

  return {
    files: scopesAndFiles?.files ?? [],
    scopes: mapScopeTags(scopesAndFiles?.scopes ?? []),
    excludedFiles: scopesAndFiles?.excludedFiles ?? [],
    excludedScopes: mapScopeTags(scopesAndFiles?.excludedScopes ?? []),
    ...(categoryFieldsForSubmit &&
      (includedScopeCats.length > 0 ||
        excludedScopeCats.length > 0 ||
        categoryFieldsForSubmit.evidenceAllFileTypes) && {
        ...categoryFieldsForSubmit,
        ...(includedScopeCats.length > 0
          ? { contentCategories: includedScopeCats }
          : { contentCategories: [] }),
      }),
  };
}
